import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useBoolean } from 'react-use'
import { Active, closestCenter, DndContext, Over } from '@dnd-kit/core'
import { restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers'
import { arrayMove, SortableContext } from '@dnd-kit/sortable'
import clsx from 'clsx'
import { v4 as uuid4 } from 'uuid'

import { AIEmojisAPI } from '@makyoapp/makyo-sdk'
import { AppBreakpoint } from '@makyoapp/makyo-sdk/models/AppBuilder'
import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { ColumnAdapter, TableAdapter, TableColumnListValue } from '@makyoapp/makyo-common'
import { Textfield as Column16, Textfield, Trash16 } from '@makyoapp/makyo-icon/icon'
import { Dialog, MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { Button, ButtonVariant, DD, MaterialIconPicker, SelectDropdown, SimpleInput, Switch } from '@makyoapp/makyo-ui'

import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useSortableHandle } from '@/hooks/useSortableHandle'
import { BuilderComponentSetting } from '@/shared/builder'
import { SHOW_SEARCH_LIMIT } from '@/shared/constants'
import { VariableWrapper } from '@/components/VariableSelectV2/VariableWrapper'

import { RenderColumn } from '../RenderColumn'
import { RepeaterButton } from '../RepeaterButton'
import { SortableItem } from '../SortableItem'
import VariableColumn from '../VariableSelectV2/VariableColumn'
import { TableDataSourceDropdown } from './TableDataSourceDropdown'

export interface TableColumnListProps {
  id: string
  activeLayerTableId?: string
  tables: TableAdapter[]
  onChange: (nextValue: TableColumnListValue) => void
  value?: TableColumnListValue
  columnTypes?: ColumnTypes[]
  noCustom?: boolean
  excludeColumnTypes?: ColumnTypes[]
  activeDevice?: AppBreakpoint
  tableColumnListSettings?: BuilderComponentSetting['tableColumnListSettings']
  selectIcon?: boolean
  showDeviceVisibility?: boolean
  showModal?: boolean
  type: 'data-table' | 'single-row'
}

const TableColumnListContext = createContext<TableColumnListProps>({
  id: '',
  onChange: () => null,
  value: undefined,
  activeLayerTableId: '',
  tables: [],
  type: 'data-table',
})

function ColumnRepeater({
  noCustom,
  excludeColumnTypes,
  activeDevice,
  selectIcon,
  showDeviceVisibility = true,
  showModal = false,
}: {
  noCustom?: boolean
  excludeColumnTypes?: ColumnTypes[]
  activeDevice?: AppBreakpoint
  selectIcon?: boolean
  showDeviceVisibility?: boolean
  showModal?: boolean
}) {
  const { id, value, onChange, columnTypes, tables, activeLayerTableId } = useContext(TableColumnListContext)
  const [showIconPicker, setShowIconPicker] = useBoolean(false)
  const [activeIconIndex, setActiveIconIndex] = useState<number | null>(null)

  const [showColumnDropdown, setShowColumnDropdown] = useBoolean(false)
  const [activeColumnIndex, setActiveColumnIndex] = useState<number | null>(null)
  const [infoInputValue, setInfoInputValue] = useState<string>('')

  const cleanTableId = (tableId?: string) => tableId?.replace('__current_user', '') || ''

  const cleanValue = useMemo(
    () => ({
      ...value,
      table: cleanTableId(value?.table),
      columns: value?.columns?.map((c) => ({
        ...c,
        col: cleanTableId(c.col),
      })),
    }),
    [value]
  )

  const dragEndCb = useCallback(
    (active: Active, over: Over) => {
      const cols = cleanValue?.columns || []
      const oldIndex = cols.findIndex((p) => p.id === active.id)
      const newIndex = cols.findIndex((p) => p.id === over.id)
      const newValue = arrayMove(cols, oldIndex, newIndex)
      onChange?.({
        ...cleanValue,
        table: cleanValue?.table || '',
        isDetailPageTable: value?.isDetailPageTable,
        columns: newValue,
      })
    },
    [cleanValue, onChange, value?.isDetailPageTable]
  )

  const { sensors, handleDragEnd } = useSortableHandle(dragEndCb)
  const { t } = useTranslation()

  const tblObj = useMemo(() => {
    return activeLayerTableId && cleanValue?.isDetailPageTable === true
      ? tables.find((tb) => tb.id === activeLayerTableId)
      : tables.find((tb) => tb.id === cleanValue?.table)
  }, [cleanValue, tables, activeLayerTableId])

  const colOptions = useMemo(() => {
    const options: ColumnAdapter[] = [{ id: '', name: 'Select Column' } as ColumnAdapter]

    if (!noCustom) {
      options.push({ id: '#custom#', name: 'Enter Custom' } as ColumnAdapter)
    }

    if (tblObj?.columns) {
      const filteredColumns = tblObj.columns.filter((c) => {
        if (columnTypes?.length && excludeColumnTypes?.length) {
          return columnTypes.includes(c.type) && !excludeColumnTypes.includes(c.type)
        }
        if (columnTypes?.length) {
          return columnTypes.includes(c.type)
        }
        if (excludeColumnTypes?.length) {
          return !excludeColumnTypes.includes(c.type)
        }
        return true
      })

      options.push(...filteredColumns)
    }

    return [{ id: '_id', name: 'Row ID', type: ColumnTypes.RowID } as ColumnAdapter, ...options]
  }, [tblObj, columnTypes, noCustom, excludeColumnTypes])

  return (
    <div id={`${id}-tcl-col-repeater`} className='flex flex-col gap-4'>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToFirstScrollableAncestor]}
      >
        <SortableContext items={cleanValue?.columns || []}>
          {(cleanValue?.columns || []).map((v, i) => {
            return (
              <SortableItem
                id={v.id}
                key={v.id}
                itemClassName='w-full'
                noBg
                onDelete={() => {
                  const nextVal = [...(cleanValue?.columns || [])]
                  nextVal.splice(i, 1)
                  onChange?.({
                    ...cleanValue,
                    table: value?.table || '',
                    isDetailPageTable: cleanValue?.isDetailPageTable,
                    columns: nextVal,
                  })
                }}
              >
                <div className='w-full ltr:mr-2 rtl:ml-2 min-w-0'>
                  <div className='flex-c w-full ltr:mr-2 rtl:ml-2 min-w-0 gap-2'>
                    <div className='flex-1 min-w-[90px] max-w-[200px]'>
                      <SimpleInput
                        id={id ? `${id}-label` : undefined}
                        value={v.label}
                        className='h-9!'
                        inputClassName='min-w-0'
                        placeholder={t('Label')}
                        onChange={(e) => {
                          const val = e.currentTarget.value
                          const nextVal = [...(cleanValue?.columns || [])]
                          nextVal[i].label = val
                          onChange?.({
                            ...cleanValue,
                            table: value?.table || '',
                            isDetailPageTable: cleanValue?.isDetailPageTable,
                            columns: nextVal,
                          })
                        }}
                      />
                    </div>
                    <SelectDropdown
                      id='tcl-col-select'
                      menuAfterClassName='gap-2 border-t bdr'
                      className='flex-2 min-w-0'
                      options={colOptions}
                      value={colOptions.find((c) => c.id === v.col)}
                      optionLabel={(opt) => `${opt?.id} ${opt?.name}`}
                      withSearch={colOptions.length > SHOW_SEARCH_LIMIT}
                      onChange={(opt) => {
                        const nextVal = [...(cleanValue?.columns || [])]
                        nextVal[i].col = opt.id
                        if (opt.id && opt.id !== '#custom#') {
                          nextVal[i].label = opt.name
                        } else {
                          nextVal[i].customValue = undefined
                        }
                        if (activeDevice) {
                          nextVal[i].visibility = {
                            ...nextVal[i].visibility,
                            [activeDevice]: true,
                          }
                        }
                        onChange?.({
                          ...cleanValue,
                          table: value?.table || '',
                          isDetailPageTable: cleanValue?.isDetailPageTable,
                          columns: nextVal,
                        })
                      }}
                      portalMenu={false}
                      menuFullWidth={false}
                      menuClassName='text-m-m'
                      menuContainerClassName='w-[260px]'
                      renderOption={(opt) => {
                        if (opt.id === '#custom#') {
                          return (
                            <div className='flex-c'>
                              <Textfield />
                              <div>
                                <Trans>Enter Custom</Trans>
                              </div>
                            </div>
                          )
                        }
                        return <RenderColumn withStyle={false} col={opt} />
                      }}
                    />
                  </div>
                  {v.col === '#custom#' && (
                    <SimpleInput
                      className='mt-2'
                      value={v.customValue || ''}
                      placeholder={t('Enter Custom')}
                      onChange={(e) => {
                        const val = e.currentTarget.value
                        const nextVal = [...(cleanValue?.columns || [])]
                        nextVal[i].customValue = val

                        onChange?.({
                          ...cleanValue,
                          table: value?.table || '',
                          isDetailPageTable: cleanValue?.isDetailPageTable,
                          columns: nextVal,
                        })
                      }}
                    />
                  )}
                </div>
                {showDeviceVisibility && (
                  <div>
                    {activeDevice ? (
                      <Button
                        icon={<MaterialIcon icon='check' size={16} />}
                        variant={v.visibility?.[activeDevice] ? ButtonVariant.Normal : ButtonVariant.Ghost}
                        title={t('Show on this screen size')}
                        noPadding
                        squared
                        onClick={() => {
                          const nextVal = [...(cleanValue?.columns || [])]

                          nextVal[i].visibility = {
                            ...nextVal[i].visibility,
                            [activeDevice]: !nextVal[i].visibility?.[activeDevice],
                          }

                          onChange?.({
                            ...cleanValue,
                            table: value?.table || '',
                            isDetailPageTable: cleanValue?.isDetailPageTable,
                            columns: nextVal,
                          })
                        }}
                      />
                    ) : (
                      // Trello card: https://trello.com/c/qCGcxppZ/577-remove-user-specific-in-single-row
                      <></>
                      // <Button
                      //   icon={<User16 />}
                      //   variant={v.userSpecific ? ButtonVariant.Normal : ButtonVariant.Ghost}
                      //   title={t('User specific')}
                      //   noPadding
                      //   squared
                      //   onClick={() => {
                      //     const nextVal = [...(cleanValue?.columns || [])]
                      //     nextVal[i].userSpecific = !nextVal[i].userSpecific
                      //     onChange?.({
                      //       ...cleanValue,
                      //       table: value?.table || '',
                      //       isDetailPageTable: cleanValue?.isDetailPageTable,
                      //       columns: nextVal,
                      //     })
                      //   }}
                      // />
                    )}
                  </div>
                )}
                {selectIcon && (
                  <MaterialIconPicker
                    id={id ? `${id}-icon-${i}` : undefined}
                    show={showIconPicker && activeIconIndex === i}
                    onClose={() => {
                      setShowIconPicker(false)
                      setActiveIconIndex(null)
                    }}
                    toggleClassName='flex-cc rounded-sm bg-zinc-200 dark:bg-slate-600 w-9 h-9'
                    toggle={
                      v.icon === '' || v.icon === undefined
                        ? (active, toggleProps) => {
                            return <div {...toggleProps}>+</div>
                          }
                        : undefined
                    }
                    onToggle={() => {
                      setShowIconPicker(true)
                      setActiveIconIndex(i)
                    }}
                    settings={{
                      opsz: 24,
                      fill: 0,
                      wght: 400,
                      grade: 0,
                    }}
                    searchMs={300}
                    searchFilter={async (keyword) => {
                      const aiSearch = await AIEmojisAPI.ReqFindEmoji(keyword)
                      return aiSearch
                    }}
                    value={v.icon ?? ''}
                    clickAsDone
                    portalMenu
                    menuClassName='z-[9999]'
                    onClear={() => {
                      const nextVal = [...(cleanValue?.columns || [])]
                      nextVal[i].icon = undefined
                      onChange?.({
                        ...cleanValue,
                        table: value?.table || '',
                        isDetailPageTable: cleanValue?.isDetailPageTable,
                        columns: nextVal,
                      })
                    }}
                    onDone={(icon) => {
                      const nextVal = [...(cleanValue?.columns || [])]
                      nextVal[i].icon = icon
                      onChange?.({
                        ...cleanValue,
                        table: value?.table || '',
                        isDetailPageTable: cleanValue?.isDetailPageTable,
                        columns: nextVal,
                      })
                      setShowIconPicker(false)
                      setActiveIconIndex(null)
                    }}
                  />
                )}
                {showModal && (
                  <Button
                    variant={ButtonVariant.Ghost}
                    noPadding
                    squared
                    icon={<MaterialIcon icon='edit' size={16} />}
                    className='flex-cc rounded-sm bg-transparent w-9 h-9 shrink-0'
                    title='Add info'
                    onClick={() => {
                      setActiveColumnIndex(i)
                      setInfoInputValue(v.info || '')
                      setShowColumnDropdown(true)
                    }}
                  />
                )}
              </SortableItem>
            )
          })}
          <RepeaterButton
            disabled={
              (activeLayerTableId && cleanValue?.isDetailPageTable === false && !cleanValue?.table) ||
              (!activeLayerTableId && !cleanValue?.table)
            }
            id={id ? `${id}-repeat` : undefined}
            onClick={() => {
              onChange?.({
                ...cleanValue,
                table: value?.table || '',
                isDetailPageTable: value?.isDetailPageTable,
                columns: [
                  ...(cleanValue?.columns || []),
                  {
                    id: uuid4(),
                    label: '',
                    col: undefined,
                    customValue: undefined,
                    userSpecific: false,
                    visibility: {
                      phone: true,
                      'phone-landscape': true,
                      tablet: true,
                      'tablet-landscape': true,
                      desktop: true,
                      tv: true,
                    },
                    icon: undefined,
                    info: undefined,
                  },
                ],
              })
            }}
          >
            <Trans>Add Column</Trans>
          </RepeaterButton>
        </SortableContext>
      </DndContext>
      {showModal && showColumnDropdown && activeColumnIndex !== null && (
        <Dialog
          open={showColumnDropdown}
          onOpenChange={(open) => {
            if (!open) {
              setShowColumnDropdown(false)
              setActiveColumnIndex(null)
              setInfoInputValue('')
            }
          }}
          title={t('Add Info')}
          titleTypography={{ typography: 'body-small' }}
          contentClassName='p-4 bg-white dark:bg-slate-200'
          footer={
            <div className='flex items-center justify-end gap-2'>
              <Button
                className='text-xs py-1 px-2'
                variant={ButtonVariant.Outlined}
                onClick={() => {
                  setShowColumnDropdown(false)
                  setActiveColumnIndex(null)
                  setInfoInputValue('')
                }}
              >
                <Trans>Cancel</Trans>
              </Button>
              <Button
                className='text-xs py-1 px-2'
                onClick={() => {
                  if (activeColumnIndex !== null) {
                    const nextVal = [...(cleanValue?.columns || [])]
                    nextVal[activeColumnIndex].info = infoInputValue.trim() || undefined

                    if (activeDevice) {
                      nextVal[activeColumnIndex].visibility = {
                        ...nextVal[activeColumnIndex].visibility,
                        [activeDevice]: true,
                      }
                    }

                    onChange?.({
                      ...cleanValue,
                      table: value?.table || '',
                      isDetailPageTable: cleanValue?.isDetailPageTable,
                      columns: nextVal,
                    })

                    setShowColumnDropdown(false)
                    setActiveColumnIndex(null)
                    setInfoInputValue('')
                  }
                }}
                disabled={false}
              >
                <Trans>Save</Trans>
              </Button>
            </div>
          }
        >
          <div className='flex flex-col gap-4 bg-white dark:bg-transparent'>
            <div className='flex flex-col gap-2'>
              <input
                id={`${id}-info-input-${activeColumnIndex}`}
                value={infoInputValue}
                placeholder={t('Enter info')}
                onChange={(e) => {
                  setInfoInputValue(e.currentTarget.value)
                }}
                className='w-full text-sm border border-neutral-40 p-4 rounded-md'
                autoFocus
              />
            </div>
          </div>
        </Dialog>
      )}
    </div>
  )
}

export function TableColumnList({
  id,
  value,
  onChange,
  tables,
  noCustom = false,
  excludeColumnTypes,
  activeDevice,
  tableColumnListSettings,
  selectIcon,
  showDeviceVisibility = true,
  showModal = false,
  type = 'data-table',
}: TableColumnListProps) {
  //
  const { activeLayerTableId } = useActiveLayerContext()

  const { columnTypes, showCurrentUserLogin } = tableColumnListSettings || {}

  const isEmptyColumn = useMemo(() => !value || value?.columns.length === 0, [value])

  const cleanTableId = (tableId?: string) => tableId?.replace('__current_user', '') || ''

  const selectedTable = tables.find((t) => t.id === value?.table)

  const tableSelectDisabled = useMemo(() => {
    if (type === 'single-row') {
      return !!value?.isDetailPageTable
    }
    return false
    //
  }, [type, value?.isDetailPageTable])

  useEffect(() => {
    if (value && Boolean(value.isDetailPageTable) && !!activeLayerTableId && value.table !== activeLayerTableId) {
      onChange({
        ...value,
        table: activeLayerTableId,
        isDetailPageTable: true,
      })
    }
  }, [value, activeLayerTableId, onChange])

  return (
    <TableColumnListContext.Provider
      value={{
        id,
        activeLayerTableId,
        tables,
        value,
        onChange,
        columnTypes,
        type,
      }}
    >
      {activeLayerTableId && (
        <label className='flex-c py-2'>
          <Trans>Use Detail Screen Data</Trans>
          <Switch
            checked={!!value?.isDetailPageTable}
            onClick={(isChecked) => {
              onChange({
                table: activeLayerTableId || '',
                isDetailPageTable: isChecked,
                columns: value?.columns || [],
              })
            }}
          />
        </label>
      )}

      <TableDataSourceDropdown
        tables={tables}
        id={`${id}-tcl-table`}
        value={value?.table}
        onChange={(nextValue) => {
          const defaultTable = tables.find((table) => table.id === cleanTableId(nextValue))
          const selectAllColumn = selectIcon && !showDeviceVisibility
          const maxColumn = selectAllColumn ? defaultTable?.columns.length : 3
          const columns = defaultTable?.columns.slice(0, maxColumn).map((c) => ({
            col: c.id,
            id: c.id,
            label: c.name,
            userSpecific: false,
            visibility: {
              phone: true,
              'phone-landscape': true,
              tablet: true,
              'tablet-landscape': true,
              desktop: true,
              tv: true,
            },
          }))
          onChange?.({
            table: nextValue,
            isDetailPageTable: value?.isDetailPageTable,
            columns: columns || [],
            filterBySession: defaultTable?.isSessionTable,
          })
        }}
        showCurrentUserLogin={showCurrentUserLogin}
        disabled={tableSelectDisabled}
      />

      {selectedTable?.isSessionTable && (
        <div className='flex-c gap-2'>
          <Trans>Filter by current session</Trans>
          <Switch
            checked={!!value?.filterBySession}
            onClick={(isChecked) => {
              onChange({
                table: value?.table || '',
                isDetailPageTable: value?.isDetailPageTable,
                columns: value?.columns || [],
                filterBySession: isChecked,
              })
            }}
          />
        </div>
      )}

      <hr className='bdr' />

      <Button
        defaultSize={false}
        variant={ButtonVariant.Ghost}
        className={clsx(
          'cursor-pointer',
          'shrink-0 w-full self-start justify-start! font-normal! bg-zinc-200! dark:bg-zinc-700! hover:bg-zinc-300 dark:!hover:bg-zinc-600 text-zinc-400! dark:text-slate-300! dark:hover:text-slate-200',
          !isEmptyColumn ? 'dark:bg-slate-600 dark:text-slate-300 shadow-none' : 'text-zinc-400 cursor-not-allowed'
        )}
        icon={<Trash16 />}
        disabled={isEmptyColumn}
        onClick={() => {
          if (isEmptyColumn) return
          onChange?.({
            table: value?.table || '',
            isDetailPageTable: value?.isDetailPageTable,
            columns: [],
          })
        }}
        noTip
      >
        <Trans>Delete All Columns</Trans>
      </Button>

      <ColumnRepeater
        noCustom={noCustom}
        excludeColumnTypes={excludeColumnTypes}
        activeDevice={activeDevice}
        selectIcon={selectIcon}
        showDeviceVisibility={showDeviceVisibility}
        showModal={showModal}
      />
    </TableColumnListContext.Provider>
  )
}
